import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, tap, catchError } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { environment } from '@env/environment';
import { Endpoint } from '@enums/endpoint.enum';
import { User } from '@models/user.model';
import { GridDataInterface, PaginationModel } from '@models/pagination.model';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  private readonly _me = signal<User | null>(null);
  readonly me = this._me.asReadonly();

  constructor(
    private http: HttpClient,
    private toastService: ToastService,
  ) {}

  getMe(): Observable<User> {
    const url = `${environment.apiBaseUrl}${Endpoint.ME}`;

    return this.http.get<{ data: any }>(url, { params: { append: 'isUnitSyncEnabled' } }).pipe(
      map(res => new User(res.data)),
      tap(user => this._me.set(user))
    );
  }

  setUser(user: User): void {
    this._me.set(user);
  }

  clear(): void {
    this._me.set(null);
  }

  getUsers(queryParams: any = {}): Observable<GridDataInterface<User>> {
    const url = `${environment.apiBaseUrl}${Endpoint.USERS}`;
    return this.http.get<any>(url, { params: queryParams }).pipe(
      map(response => ({
        data: (response.data ?? []).map((u: any) => new User(u)),
        pagination: PaginationModel.fromApi(response.meta?.pagination),
      })),
      catchError(error => {
        this.toastService.showDanger(error?.error?.message || 'Failed to load users');
        return of({ data: [], pagination: new PaginationModel() });
      })
    );
  }

  save(data: any, profileImage?: File | null, removeProfileImage = false): Observable<User | null> {
    const isEdit = !!data.id;
    const url = isEdit
      ? `${environment.apiBaseUrl}${Endpoint.USERS}/${data.id}`
      : `${environment.apiBaseUrl}${Endpoint.USERS}`;

    if (isEdit && !data.password) {
      delete data.password;
    }

    if (profileImage || removeProfileImage) {
      const formData = new FormData();

      Object.entries(data).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(item => formData.append(`${key}[]`, String(item)));
        } else if (value !== null && value !== undefined) {
          formData.append(key, value as string);
        }
      });

      if (profileImage) {
        formData.append('profile_image', profileImage);
      }

      if (removeProfileImage) {
        formData.append('remove_profile_image', '1');
      }

      if (isEdit) {
        formData.append('_method', 'PATCH');
      }

      return this.http.post(url, formData).pipe(
        map((response: any) => response?.data ? new User(response.data) : null)
      );
    }

    const request$ = isEdit
      ? this.http.patch(url, data)
      : this.http.post(url, data);

    return request$.pipe(
      map((response: any) => response?.data ? new User(response.data) : null)
    );
  }

  saveProfile(data: any, profileImage?: File | null, removeProfileImage = false): Observable<User | null> {
    const url = `${environment.apiBaseUrl}${Endpoint.PROFILE}`;

    if (!data.password) {
      delete data.password;
    }

    if (profileImage || removeProfileImage) {
      const formData = new FormData();

      Object.entries(data).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          formData.append(key, value as string);
        }
      });

      if (profileImage) {
        formData.append('profile_image', profileImage);
      }

      if (removeProfileImage) {
        formData.append('remove_profile_image', '1');
      }

      formData.append('_method', 'PATCH');

      return this.http.post(url, formData).pipe(
        map((response: any) => response?.data ? new User(response.data) : null)
      );
    }

    return this.http.patch(url, data).pipe(
      map((response: any) => response?.data ? new User(response.data) : null)
    );
  }

  deleteUser(id: number): Observable<void> {
    const url = `${environment.apiBaseUrl}${Endpoint.USERS}/${id}`;
    return this.http.delete<void>(url);
  }
}
